import React, { FC, useContext, useMemo, useRef, useCallback, useState, useLayoutEffect } from "react";
import { observer } from "mobx-react-lite";
import More from "../../../assets/moreGray.svg";
import MoreGray from "../../../assets/moreGrayYes.svg";
import ArrowRight from "../../../assets/ArrowRight.svg";
import ArrowDown from "../../../assets/arrowDown.svg";
import "../../styles/popUp.pc.css";
import useOutsideAlerter from "../../../hooks/useOutsideAlert";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { AppContext } from "../../../App";
import { hasPermission } from "../../../helpers/hasPermisson";
import { BoardContext, IBoardForProxy } from "../../../contexts/BoardContext";
import { IColumnDisplay } from "../../../common/api_interfaces/column/IColumnDisplay";
import { IRestrictBoardView } from "../../../common/api_interfaces/board/IRestrictBoardView";

export interface IMoreButtonProps {
    onClickFunc: (e: React.MouseEvent) => void;
    data: string[];
    typeForKey: string;
    open: boolean;
    setOpen: (open: boolean) => void;
    columnsChoices?: IColumnDisplay[];
    boardChoices?: IBoardForProxy[];
    taskId?: number;
    userAccess: IFunctionPermissions[];
    columnAccess: boolean;
    boardsData?: IRestrictBoardView | undefined;
}

const BtnMore: FC<IMoreButtonProps> = observer(({
    onClickFunc,
    data,
    typeForKey,
    open,
    setOpen,
    columnsChoices,
    boardChoices,
    taskId,
    userAccess,
    columnAccess,
    boardsData,
}) => {
    const boardContext = useContext(BoardContext);
    const { showToast, currentZoom } = useContext(AppContext);

    const zoom = useMemo(() => Math.round(currentZoom * 100) / 100, [currentZoom]);
    
    const editTask = useMemo(() => 
        hasPermission(userAccess, "TaskAction", "edit"),
    [userAccess]);
    
    const delTask = useMemo(() => 
        hasPermission(userAccess, "TaskAction", "delete"),
    [userAccess]);
    
    const columns = useMemo(() => 
        boardsData?.boards.find(xx => xx.id === boardContext.boardIdNumber)?.columns,
    [boardsData, boardContext.boardIdNumber]);

    const outsideAlerterRef = useOutsideAlerter<HTMLDivElement>(() => {setOpen(false); setShowColumns(false);});
    const menuWrapperRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const innerMenuRef = useRef<HTMLDivElement>(null);

    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [innerMenuPosition, setInnerMenuPosition] = useState({ top: 0, left: 0 });
    const [showColumns, setShowColumns] = useState<boolean>(false);

    const handleMoveToColumn = useCallback((columnId: number) => {
        if (taskId) {
            boardContext.handleMoveTask(taskId, columnId, 0);
        }
    }, [taskId, boardContext.handleMoveTask]);

    const handleMoveToBoard = useCallback((boardId: number) => {
        if (taskId) {
            boardContext.handleMoveTaskBoard(taskId, boardId);
        }
    }, [taskId, boardContext.handleMoveTaskBoard]);

    const handleMenuItemClick = useCallback((e: React.MouseEvent, item: string) => {
        e.stopPropagation();
        
        switch (item) {
            case "Редактировать":
                if (!editTask) {
                    showToast("У вас нет прав для редактирования задачи!");
                    return;
                }
                break;
            case "Удалить":
                if (!delTask) {
                    showToast("У вас нет прав для удаления задачи!");
                    return;
                }
                break;
        }
        
        onClickFunc(e);
    }, [editTask, delTask, showToast, onClickFunc]);


    const calculateMenuPosition = () => {
        const rectButton = outsideAlerterRef.current?.getBoundingClientRect();
        const menuWrapperRect = menuWrapperRef.current?.getBoundingClientRect();
        const menuRect = menuRef.current?.getBoundingClientRect();
        const innerMenuRect = innerMenuRef.current?.getBoundingClientRect();

        if (rectButton && menuWrapperRect && menuRect) {
            let topInnerMenu = 10000;
            let leftInnerMenu = 10000;
            let windowWidth = window.innerWidth;
            let windowHeight= window.innerHeight;
            let topMenu, leftMenu;

            const widthLeft = rectButton.left + rectButton.width + menuRect.width;
            const widthTop = rectButton.top + menuRect.height;

            // Основное меню
            if (widthTop > windowHeight) {
                topMenu = Math.round(rectButton.top / zoom - menuRect.height / zoom + rectButton.height);
            } else {
                topMenu = Math.round(rectButton.top / zoom);
            }

            if (widthLeft > windowWidth) {
                leftMenu = Math.round(rectButton.left / zoom - menuRect.width / zoom);
            } else {
                leftMenu = Math.round(rectButton.left / zoom + rectButton.width / zoom);
            }

            // Саб меню
            if (innerMenuRect) {
                topInnerMenu = Math.round((menuRect.top / zoom + menuRect.height / zoom) - menuRect.height / zoom / 2);
                
                const innerWidthLeft = menuRect.left + menuRect.width + innerMenuRect.width;
                const innerWidthTop = menuRect.top + menuRect.height + innerMenuRect.height;

                if (innerWidthTop > windowHeight) {
                    topInnerMenu = Math.round((menuRect.top / zoom) - innerMenuRect.height / zoom + menuRect.height / zoom / 2);
                } else {
                    topInnerMenu = Math.round((menuRect.top / zoom + menuRect.height / zoom) - menuRect.height / zoom / 2);
                }
                
                if (innerWidthLeft > windowWidth) {
                    leftInnerMenu = Math.round((leftMenu - innerMenuRect.width / zoom));
                } else {
                    leftInnerMenu = Math.round((leftMenu + innerMenuRect.width / zoom));
                }
            }

            setMenuPosition({ top: topMenu, left: leftMenu });
            setInnerMenuPosition({ top: topInnerMenu, left: leftInnerMenu });
        }
    };

    useLayoutEffect(() => {
        if (!open) return;

        const animationFrameId = requestAnimationFrame(calculateMenuPosition);
        return () => cancelAnimationFrame(animationFrameId);
    }, [open, zoom, showColumns]);

    const renderMenuItem = useCallback((item: string) => {
        switch (item) {
            case "Редактировать":
            case "Удалить":
                return (
                    <div
                        key={item}
                        className="popUpItem"
                        onClick={(e) => handleMenuItemClick(e, item)}
                    >
                        <div className="p_12_popUP">{item}</div>
                    </div>
                );
                
            case "Переместить":
                return (
                    <div
                        key={item}
                        className="popUpItem"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowColumns(prev => !prev);
                        }}
                    >
                        <div className="p_12_popUP">
                            {item}
                            <img
                                src={showColumns ? ArrowDown : ArrowRight}
                                height="16px"
                                style={{ float: "right" }}
                                alt="arrow"
                            />
                        </div>
                    </div>
                );
                
            default:
                return (
                    <div key={item} className="popUpItem">
                        <div
                            className="p_12_popUP"
                            onClick={(e) => onClickFunc(e)}
                        >
                            {item}
                        </div>
                    </div>
                );
        }
    }, [editTask, delTask, showToast, onClickFunc, showColumns, setShowColumns]);

    return (
        <div
            key={typeForKey}
            className="button-base"
            ref={outsideAlerterRef}
            onClick={() => columnAccess && setOpen(true)}
        >
            <div>
                <img src={!columnAccess ? MoreGray : More} draggable="false" alt="more" />
            </div>
            
            {open && (
                <div
                    className="popUpWrapper"
                    style={{
                        top: `${menuPosition.top}px`,
                        left: `${menuPosition.left}px`,
                    }}
                    ref={menuWrapperRef}
                >
                    <div className="popUpContainerMore" ref={menuRef}>
                        {data.map(renderMenuItem)}
                    </div>

                    {showColumns && (
                        <div 
                            className="popUpWrapper_inner"
                            style={{
                                top: `${innerMenuPosition.top}px`,
                                left: `${innerMenuPosition.left}px`,
                            }}
                        >
                            <div
                                className="popUpContainerMore"
                                style={{
                                    maxHeight: "300px",
                                    overflow: "auto"
                                }}
                                ref={innerMenuRef}
                            >
                                <label className="popUpLabel">Переместить в колонку</label>
                                {columnsChoices?.map((column) => 
                                    (!columns || columns.find(yy => yy.id === column.id)) && (
                                        <div
                                            key={column.id}
                                            className="popUpItem_inner p_12_popUP"
                                            onClick={() => handleMoveToColumn(column.id)}
                                        >
                                            {column.name.length > 20 
                                                ? `${column.name.slice(0, 20)}...` 
                                                : column.name
                                            }
                                        </div>
                                    )
                                )}
                                
                                <label className="popUpLabel">На доску</label>
                                {boardChoices?.map((board) => (
                                    <div
                                        key={board.id}
                                        className="popUpItem_inner p_12_popUP"
                                        onClick={() => handleMoveToBoard(board.id)}
                                    >
                                        {board.name.length > 20 
                                            ? `${board.name.slice(0, 20)}...` 
                                            : board.name
                                        }
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default BtnMore;